import React, { useCallback, useEffect, useState } from 'react';
import { GenericModalDialog } from '../../../modalDialog/GenericModalDialog';
import { Stack, Box, CircularProgress, Table, TableHead, TableRow, TableBody, Paper, Typography, Checkbox, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SessionState } from '../../../../reducers/auth/session';
import { AppState } from '../../../../reducers';
import { connect, useDispatch } from 'react-redux';
import { leadingZeroes } from '../../../../utils/StringUtils';
import AutocompleteSelector from '../../../Utils/AutocompleteSelector';
import { VendorListState } from '../../../../reducers/vendor/vendorList';
import { LinkedVendorsListState } from '../../../../reducers/person/linkedVendorsList';
import { fetchVendors } from '../../../../actions/vendor/vendorList';
//import Vendor from '../../../../models/Vendor';
import { fetchLinkedVendors } from '../../../../actions/person/linkedVendorsList';
import { ISTableCell, ISTableHeaderCell, ISTableRow } from '../../../Utils/ISTable';
import { DeleteForeverOutlined, DeleteOutlineOutlined } from '@mui/icons-material';
import { deleteAllLinkedVendors, deleteLinkedVendors } from '../../../../actions/person/linkedVendorsDelete';
import { upsertLinkedVendor } from '../../../../actions/person/linkedVendorsUpsert';
import { getToken } from '../../../../utils/fetchApi'
import { PersonVendorObjectiveVendor, PersonVendorObjectiveConfigResponse } from '../../../../models/PersonVendorObjectiveConfig'

interface LinkedVendorsProps {
  onConfirm: () => void
  onCancel: () => void
  personId: number
  vendorList?: VendorListState
  linkedVendors?: LinkedVendorsListState
  session?: SessionState
}


export const LinkedVendorsDialog: React.FC<LinkedVendorsProps> = (props) => {
  const { t } = useTranslation(['person', 'menu'])
  const dispatch = useDispatch()
  const [goalVendors, setGoalVendors] = useState<PersonVendorObjectiveVendor[]>([])
  const [goalConfigLoading, setGoalConfigLoading] = useState(false)
  const [goalConfigChanged, setGoalConfigChanged] = useState(false)
  //const [vendorInfo, setVendorInfo] = useState<Vendor | undefined>(undefined)

  const fetchGoalConfig = useCallback(async () => {
    setGoalConfigLoading(true)

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/objectives/person-vendor-config?personId=${props.personId}&access_token=${getToken()}`)
      const data: PersonVendorObjectiveConfigResponse = await response.json()
      setGoalVendors(data.people?.[0]?.vendors || [])
      setGoalConfigChanged(false)
    } finally {
      setGoalConfigLoading(false)
    }
  }, [props.personId])

  const onClickDelete = async (linkedVendorsId: number) => {
    await dispatch(deleteLinkedVendors(linkedVendorsId))
    await dispatch(fetchLinkedVendors(`?personId=${props.personId}`))
    await fetchGoalConfig()
  }

  const onClickDeleteAll = async () => {
    await dispatch(deleteAllLinkedVendors(props.personId))
    await dispatch(fetchLinkedVendors(`?personId=${props.personId}`))
    await fetchGoalConfig()
  }

  const onChangeGoalParticipation = (vendorId: number, field: 'participatesObjective1' | 'participatesObjective2', checked: boolean) => {
    setGoalVendors(current => current.map(v => v.vendorId === vendorId ? { ...v, [field]: checked } : v))
    setGoalConfigChanged(true)
  }

  const getGoalVendor = (vendorId: number) => goalVendors.find(v => v.vendorId === vendorId)

  const saveGoalConfig = async () => {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/objectives/person-vendor-config/${props.personId}?access_token=${getToken()}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        vendors: goalVendors.map(v => ({
          vendorId: v.vendorId,
          participatesObjective1: v.participatesObjective1,
          participatesObjective2: v.participatesObjective2
        }))
      })
    })

    if (!response.ok) throw new Error('Error guardando configuración de objetivos')

    setGoalConfigChanged(false)
  }

  const onClickAdd = async (vendor: any) => {
    if (vendor && !props.linkedVendors?.linkedVendors?.find(lv => lv.vendorId === vendor.codigo_vendedor)) {
      await dispatch(upsertLinkedVendor({ id: null, vendorId: vendor.codigo_vendedor, personId: props.personId }))
      await dispatch(fetchLinkedVendors(`?personId=${props.personId}`))
      await fetchGoalConfig()
    }
  }

  useEffect(() => {
    dispatch(fetchVendors('?all=true'))
    dispatch(fetchLinkedVendors(`?personId=${props.personId}`))
    fetchGoalConfig()
  }, [dispatch, props.personId, fetchGoalConfig])

  const tooltipText = (text: string) =>
    <Paper elevation={1} sx={{ padding: "5px" }}>
      <Typography >{text}</Typography>
    </Paper>


  return <GenericModalDialog isOpen={true}
    dialogProps={{ open: true, maxWidth: "lg" }}
    onConfirm={() => props.onConfirm && props.onConfirm()}
    onCancel={() => props.onCancel && props.onCancel()}
    title={t('linkedVendors')}>
    <Stack style={{ display: 'flex', flexDirection: 'column', width: "700px" }}>
      {props.linkedVendors &&
        <Stack textAlign="center">
          <Box style={{ justifyContent: 'center' }}>
            <Table size="medium" stickyHeader={true}>
              <TableHead >
                <TableRow selected={true}>
                  <ISTableHeaderCell align='center'>{t('code')}</ISTableHeaderCell>
                  <ISTableHeaderCell>{t('name')}</ISTableHeaderCell>
                  <ISTableHeaderCell align='center' sx={{ cursor: 'pointer', padding: '0px' }}
                    tooltip={tooltipText(t('deleteAll'))}

                    onClick={(e) => { e.stopPropagation(); onClickDeleteAll() }}>
                    <DeleteForeverOutlined />
                  </ISTableHeaderCell>
                  <ISTableHeaderCell align="center">Objetivo 1</ISTableHeaderCell>
                  <ISTableHeaderCell align="center">Objetivo 2</ISTableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {props.linkedVendors && props.linkedVendors.linkedVendors &&
                  props.linkedVendors.linkedVendors.sort((a, b) => a && b ? (b.id! - a.id!) : 0).map((row, index) => {
                    const config = getGoalVendor(row.vendorId)
                    return <ISTableRow key={index} color='default' sx={{ lineHeight: "0.5rem", fontSize: "12px" }}>
                      <ISTableCell align='center'>{leadingZeroes(row.vendorId.toString(), 6)}</ISTableCell>
                      <ISTableCell>{props.vendorList?.vendors?.find(v => v.codigo_vendedor === row.vendorId)?.nombre}</ISTableCell>
                      <ISTableCell tooltip={tooltipText(t('delete'))}
                        align='center' sx={{ cursor: 'pointer', lineHeight: "0.5rem", fontSize: "12px" }}
                        onClick={(e) => { e.stopPropagation(); row.id && onClickDelete(row.id) }}>
                        <DeleteOutlineOutlined />
                      </ISTableCell>
                      <ISTableCell align="center">
                        <Checkbox
                          checked={config?.participatesObjective1 || false}
                          disabled={goalConfigLoading || !config}
                          onChange={e => onChangeGoalParticipation(row.vendorId, 'participatesObjective1', e.target.checked)}
                        />
                      </ISTableCell>

                      <ISTableCell align="center">
                        <Checkbox
                          checked={config?.participatesObjective2 || false}
                          disabled={goalConfigLoading || !config}
                          onChange={e => onChangeGoalParticipation(row.vendorId, 'participatesObjective2', e.target.checked)}
                        />
                      </ISTableCell>
                    </ISTableRow>
                  })}
              </TableBody>
            </Table>
          </Box>

        </Stack>}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 1, mt: 1, mb: 1 }}>
        <Button sx={{ textTransform: 'none' }} variant="contained" disabled={!goalConfigChanged || goalConfigLoading} onClick={saveGoalConfig}>
          Guardar participación
        </Button>
      </Box>
      {props.vendorList &&
        !props.vendorList?.isFetching &&
        !props.linkedVendors?.isFetching &&
        <Box style={{ display: 'flex', justifyContent: 'center' }}>
          <AutocompleteSelector value={undefined} onDataChange={onClickAdd} name="vendors" label={t("vendors")} style={{ maxWidth: "600px", width: "600px", margin: "10px 10px" }}
            menuItems={props.vendorList?.vendors}
            getOptionLabel={(option:
              {
                codigo_vendedor: string,
                nombre: string,
              }) => {
              return `${option?.nombre} - [${leadingZeroes(option?.codigo_vendedor, 6)}]`
            }}
            isOptionEqualToValue={(option, value) =>
              option?.codigo_vendedor === value?.codigo_vendedor
            }
            required />

        </Box>
      }
      {(props.vendorList?.isFetching ||
        props.linkedVendors?.isFetching) &&
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={25} />
        </div>
      }
    </Stack>
  </GenericModalDialog>
}


const mapStateToProps = (state: AppState) => ({
  vendorList: state.vendorList,
  linkedVendors: state.linkedVendorsList,
  session: state.session
})

export default connect(mapStateToProps, null)(LinkedVendorsDialog)